import type {CopilotChatState} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import {createContext, use} from 'react'

/**
 * The whole-value chat state context.
 *
 * It lives beside the observable chat context rather than in `copilot-chat` so
 * surfaces below Copilot chat can read the state when they happen to be inside
 * a chat tree, without importing the chat tree to do it.
 */
export const ChatStateContext = createContext<CopilotChatState | null>(null)

/**
 * Returns the current version of the chat state.
 * This causes the component to re-render any time there is an update to any part of the state.
 * Our goal is to deprecate and remove this hook.
 *
 * @example
 * ```ts
 * const {messages, customInstructions} = useChatState()
 * ```
 */
export function useChatState(): CopilotChatState {
  const chatState = use(ChatStateContext)
  if (!chatState) throw new Error('useChatState can only be used inside a CopilotChatProvider')
  return chatState
}

/**
 * Returns the current version of the chat state or undefined if not within a CopilotChatProvider.
 * This is a safe version of useChatState that won't throw an error when used outside
 * of a chat context. Useful for components that may or may not be rendered within
 * Copilot Chat.
 *
 * @example
 * ```ts
 * const chatState = useSafeChatState()
 * const isImmersiveChat = chatState?.mode === 'immersive'
 * ```
 */
export function useSafeChatState(): CopilotChatState | undefined {
  const chatState = use(ChatStateContext)
  return chatState ?? undefined
}
